import { Flex, Text } from '@chakra-ui/react';
import { FileUpload, FormFeedback } from '@morressier/alembic';
import { FormFieldFileUploadArray } from '@publishing-engine/domain/types/article';
import { AxiosProgressEvent } from 'axios';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import axiosInstance from 'src/api/client';
import { deleteUploadedFile, getSignedPrivateUploadUrl } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';
import { getFileExtension, parseUploadError } from 'src/utils/file';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import { FilesUploadedCards } from 'src/components/FormField/FileCards';
import { useDraftResponse } from 'src/contexts/uploadDraftProvider';
import { useCommandResponse } from 'src/contexts/SourceFileCommandResponseContex';
import { get } from 'lodash';
interface SourceFileUploadProps {
  config: Omit<FormFieldFileUploadArray, 'component'>;
  submissionContextId?: string;
  submissionId?: string;
}

export type IFileUploaded = {
  fileId?: string;
  url: string;
  name: string;
  referenceName: string;
  isUploading: boolean;
  progress: number;
  extension: string;
};

export const SourceFileUpload: React.FC<SourceFileUploadProps> = ({
  config,
  submissionId
}) => {
  const {
    control,
    setError,
    formState: { errors },
    register,
    watch,
    trigger
  } = useFormContext<{ [key: string]: IFileUploaded[] }>();
  const {
    caseDataFields: { main: field },
    params: { labels, validations }
  } = config;
  const mainError = get(errors, field) as FieldError;
  const { append, remove, update, fields } = useFieldArray({
    control,
    name: field
  });

  const draftContext = useDraftResponse();
  const setDraftResponse = draftContext?.setDraftResponse;

  const commandContext = useCommandResponse();
  const setCommandResponse = commandContext?.setCommandResponse;

  const getUploadUrl = (extension: string) => {
    if (!submissionId) {
      throw new Error(
        'FormFieldMultipleFileUpload: no submission id. ' + field
      );
    }

    return getSignedPrivateUploadUrl(submissionId, extension);
  };

  const uploadFile = async (file: File, index: number) => {
    const fileExtension = getFileExtension(file.name);
    let fileToUpload: IFileUploaded = {
      name: file.name,
      referenceName: file.name,
      progress: 0,
      isUploading: true,
      extension: fileExtension,
      url: ''
    };

    const onUploadProgress = () => (progressEvent: AxiosProgressEvent) => {
      const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
      const progress = Math.floor((loaded * 100) / total);

      fileToUpload = {
        ...fileToUpload,
        progress
      };

      update(index, fileToUpload);
    };

    try {
      // Only append if this index doesn't exist (i.e. new file)
      if (!fields[index]) {
        append(fileToUpload); // only append new file
      } else {
        update(index, fileToUpload); // replace existing one
      }
      const uploadParams = await getUploadUrl(fileExtension);
      if (!uploadParams) return;

      const formData = new FormData();
      formData.append('file', file, file.name);

      const {
        data: { id, file_url: url, original_filename }
      } = await axiosInstance.put<{
        id: string;
        file_url: string;
        original_filename: string;
      }>(uploadParams.signed_url, formData, {
        onUploadProgress: onUploadProgress()
      });

      fileToUpload = {
        ...fileToUpload,
        fileId: id,
        url,
        name: original_filename,
        referenceName: original_filename,
        extension: fileExtension
      };

      update(index, fileToUpload);
    } catch (e) {
      const message = parseUploadError(e);
      setError(`${field}.${index}`, {
        type: 'upload-error',
        message
      });
    } finally {
      update(index, {
        ...fileToUpload,
        isUploading: false
      });
      trigger(field);
    }
  };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    setDraftResponse?.(null);
    setCommandResponse?.(null);
    const accepted = acceptedFiles;
    await Promise.all(
      accepted.map((file, index) => uploadFile(file, index + fields.length))
    );
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    for (let index = 0; index < rejectedFiles.length; index++) {
      const file = rejectedFiles[index]!;
      const rejectedFileError = file.errors[0];
      // Compute correct index after existing files
      const errorIndex = fields.length + index;
      update(index + fields.length, {
        name: file.file.name,
        url: '',
        isUploading: false,
        progress: 0,
        referenceName: '',
        extension: ''
      });
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(`${field}.${index}`, {
          type: 'upload-error',
          message: 'An unsupported file format selected.'
        });
      }
      if (rejectedFileError?.code === ErrorCode.FileTooLarge) {
        setError(`${field}.${errorIndex}`, {
          type: 'upload-error',
          message: `File is larger than ${validations.maxSizeMB}MB`
        });
      }
    }
  };

  const removeFile = async (index: number, fileId?: string) => {
    setDraftResponse?.(null);
    setCommandResponse?.(null);
    remove(index);
    fileId && deleteUploadedFile(fileId);
  };

  const selectedFile = watch(field);
  const fileName = selectedFile?.length > 0 && selectedFile[0];
  const isDocFileSelected = fileName && /\.(docx?)$/i.test(fileName.name);

  const handleAllowFileUpload = () => {
    const filtered = { ...validations.allowedFileFormats }; // shallow copy to avoid mutating the original

    if (isDocFileSelected) {
      delete filtered['application/x-latex'];
    }

    return filtered;
  };

  return (
    <Flex
      flexDirection="column"
      gap={8}
      width={'100%'}
      wordBreak={'break-word'}
      whiteSpace={'normal'}
    >
      <Flex flexDirection="column" gap={2}>
        <Flex flexDirection="column">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
            helpText={labels.helpText}
          />
        </Flex>
        <Flex flexDirection="column" bgColor="surfacePrimaryFill">
          <FileUpload
            {...register(field)}
            uploadButtonText="Select file"
            onDrop={onDrop}
            onDropRejected={onDropRejected}
            multiple={false}
            maxFiles={1}
            maxSize={validations.maxSizeMB * 1000000}
            isInvalid={Boolean(mainError?.message)}
            accept={
              isDocFileSelected
                ? handleAllowFileUpload()
                : validations.allowedFileFormats
            }
            isReadOnly={fields[0]?.extension === 'tex'}
          />
        </Flex>
        <Text textStyle="textContext">
          Max size: {validations.maxSizeMB}MB. Executable scripts are not permitted.
        </Text>
        {mainError?.message && (
          <FormFeedback variant="error">{mainError.message}</FormFeedback>
        )}
      </Flex>
      <Flex flexDirection="column">
        {fields && fields.length >= 1 && (
          <FilesUploadedCards
            files={fields}
            handleRemove={(index, fileId) => {
              removeFile(index, fileId);
            }}
            submissionId={submissionId}
            uploadFile={uploadFile}
            field={field}
          />
        )}
      </Flex>
    </Flex>
  );
};
